import {
	NormalizedTransfer,
	TRANSFER_HEADER_BYTES,
	TRANSFER_HEADER_ENCRYPT,
	TRANSFER_HEADER_JSON,
} from "./flags";
import { decodeCompact, encodeCompact, isCompactPayload } from "./compact";

export type TransferCodecOptions = {
	transfer: NormalizedTransfer;
	secret?: string;
	sessionId: string;
	key?: Uint8Array;
};

type WireData =
	| string
	| ArrayBuffer
	| Uint8Array
	| DataView
	| Blob
	| ArrayBufferView;

function toBytes(raw: WireData): Uint8Array {
	if (typeof raw === "string") {
		return new TextEncoder().encode(raw);
	}
	if (raw instanceof ArrayBuffer) {
		return new Uint8Array(raw);
	}
	if (ArrayBuffer.isView(raw)) {
		return new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength);
	}
	throw new Error("Unsupported transfer payload");
}

function withHeader(header: number, payload: Uint8Array): Uint8Array {
	const out = new Uint8Array(payload.length + 1);
	out[0] = header;
	out.set(payload, 1);
	return out;
}

function utf8(buf: Uint8Array): string {
	return new TextDecoder().decode(buf);
}

function decodeBody(body: Uint8Array): object {
	if (isCompactPayload(body)) {
		try {
			return decodeCompact(body);
		} catch {
			// old bytes: UTF-8 JSON after the header
		}
	}
	return JSON.parse(utf8(body));
}

export function encodeTransfer(
	data: object,
	options: TransferCodecOptions,
): string | Uint8Array {
	const { encrypt, bytes } = options.transfer;

	if (encrypt) {
		throw new Error(
			"PRPCOW encrypt transfer is not supported in the web client",
		);
	}

	if (bytes) {
		return withHeader(TRANSFER_HEADER_BYTES, encodeCompact(data));
	}

	return JSON.stringify(data);
}

export function decodeTransfer(
	raw: WireData,
	_options: TransferCodecOptions,
): object {
	if (typeof raw === "string") {
		return JSON.parse(raw);
	}

	const buf = toBytes(raw);
	if (buf.length === 0) {
		throw new Error("Empty transfer payload");
	}

	const header = buf[0];
	if (header === TRANSFER_HEADER_JSON || header === "{".charCodeAt(0)) {
		let text = utf8(buf);
		if (header === TRANSFER_HEADER_JSON) {
			text = utf8(buf.subarray(1));
		}
		return JSON.parse(text);
	}

	if (header === TRANSFER_HEADER_BYTES) {
		return decodeBody(buf.subarray(1));
	}

	if (header === TRANSFER_HEADER_ENCRYPT) {
		throw new Error(
			"PRPCOW encrypt transfer is not supported in the web client",
		);
	}

	return JSON.parse(utf8(buf));
}
